import { AuthSession, fetchAuthSession, fetchUserAttributes, FetchUserAttributesOutput, getCurrentUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import { Hub } from "aws-amplify/utils";
import { Schema } from "../../amplify/data/resource";
import { syncRevenueCatUserDetails, updatePurchasesUser } from "./revenuecat/RCUserUtils";

enum UserStatus {
    ACTIVE = "ACTIVE",
    DELETED = "DELETED"
}

export type PublicUserProfile = {
    userId: string,
    userIdentityId: string,
    nickname: string
}

const listener = async (data: {
    payload: {
        data: any; event: any;
    };
}) => {
    switch (data.payload.event) {
        case 'signedIn': {
            // This is called on user login
            // Even if it first user login during account creation!
            // Can be called with empty data
            const payloadData = data.payload?.data;
            if (!payloadData?.userId) break;

            console.info('User signed data: ', data);
            console.info('User signed loginId (email): ', payloadData.signInDetails?.loginId);
            console.info('User signed userId: ', payloadData.userId);
            console.info('User signed userName (== userId): ', payloadData.username);

            PublicUserProfileUtils.upsertUserStatus(
                payloadData.userId,
                UserStatus.ACTIVE,
                Date.now()
            )
                .then(async () => {
                    try {
                        const user = await getCurrentUser();
                        updatePurchasesUser(user);
                        const attrs = await fetchUserAttributes();
                        const email = attrs?.email ?? payloadData?.signInDetails?.loginId;
                        const syncResult = await syncRevenueCatUserDetails(user, {
                            nickname: attrs?.nickname ?? undefined,
                            email: email ?? undefined,
                        });
                        console.info('[PublicUserProfileUtils] syncRevenueCatUserDetails result:', syncResult);
                    } catch (e) {
                        console.warn('[PublicUserProfileUtils] syncRevenueCatUserDetails failed:', e);
                    }
                })
                .catch(() => {});
            break;
        }
        case 'signUp':
            console.warn('user signed up');
            break;
        case 'signedOut':
            // This is called on user logout
            // But when user is deleted only sign out is called, no other env
            console.warn('user signed out');
            updatePurchasesUser(null);

            // Attributes cannot be fetched, user is signed out
            try {
                const signedOutUserAttributes: FetchUserAttributesOutput = await fetchUserAttributes()
                console.warn("Current user attributes: ", signedOutUserAttributes);
            } catch (e) {
                console.warn("Exception caught: ", e);

            }
            break;
        case 'signIn_failure':
            console.warn('user sign in failed');
            break;
        case 'configured':
            console.warn('the Auth module is configured');
            break;
        case 'tokenRefresh':
            console.warn('User token refresh called');
            if (data.payload?.data?.userId) {
                PublicUserProfileUtils.upsertUserStatus(
                    data.payload.data.userId,
                    UserStatus.ACTIVE,
                    Date.now()
                );
                try {
                    const user = await getCurrentUser();
                    updatePurchasesUser(user);
                } catch (e) {
                    console.warn('[PublicUserProfileUtils] updatePurchasesUser on tokenRefresh failed:', e);
                }
            }
            break;
        default:
            console.error('Event: ', data.payload.event);
            console.error('Something went wrong, look at data object', data);
    }
}

Hub.listen('auth', listener);

const dbClient = generateClient<Schema>()

export default class PublicUserProfileUtils {

    public static handleUserDeletion = async (userId: string) => {
        // Called properly with id from deletion onSuccess()
        // TODO only update public profile status
        console.warn("User deleted: ", userId);

        const { data: deletedUserProfile, errors: deletedUserProfileErrors } = await dbClient.models.UserPublicProfile.update(
            {
                id: userId,
                status: UserStatus.DELETED
            },
            // Options
            {
                authMode: 'userPool'
            }
        )

        if (deletedUserProfileErrors) {
            console.error("Deleted user profile errors: ", deletedUserProfileErrors);

        }

        console.warn("Deleted Profile from DB: ", deletedUserProfile);
    }

    // Not private due to being used in listener
    static upsertUserStatus = async (
        userId: string,
        status: UserStatus,
        lastLogin: number
    ) => {

        // This gets attributes when user signed in
        const currentUserAttributes: FetchUserAttributesOutput = await fetchUserAttributes()
        const currentAuthSession: AuthSession = await fetchAuthSession();

        console.warn("Current user attributes: ", currentUserAttributes);
        console.warn("Current auth session: ", currentAuthSession);


        // foundUserProfile is null if nothing empty
        var foundUserProfile = await this.getPublicProfileByUserIdFromDb(true, userId);

        console.warn("User Profile from DB: ", foundUserProfile);

        if (foundUserProfile == null) {

            const { data: createdUserProfile, errors: createdUserProfileErrors } = await dbClient.models.UserPublicProfile.create(
                {
                    id: userId,
                    userId: userId,
                    userIdentityId: currentAuthSession.identityId,
                    nickname: currentUserAttributes.nickname,
                    status: status,
                    lastLogin: lastLogin
                },
                // Options
                {
                    authMode: 'userPool'
                }

            )

            if (createdUserProfileErrors) {
                console.error("Create user profile errors: ", createdUserProfileErrors);

            }

            console.warn("Created User Profile: ", createdUserProfile);
        } else {
            // Profile exists - update userIdentityId if it's missing or different
            // This ensures that if a user uploads a profile image after creating a place,
            // their userIdentityId will be updated on next login/token refresh
            if (!foundUserProfile.userIdentityId || foundUserProfile.userIdentityId !== currentAuthSession.identityId) {
                const { data: updatedUserProfile, errors: updatedUserProfileErrors } = await dbClient.models.UserPublicProfile.update(
                    {
                        id: userId,
                        userIdentityId: currentAuthSession.identityId,
                        nickname: currentUserAttributes.nickname || foundUserProfile.nickname,
                        status: status,
                        lastLogin: lastLogin
                    },
                    // Options
                    {
                        authMode: 'userPool'
                    }
                )

                if (updatedUserProfileErrors) {
                    console.error("Update user profile errors: ", updatedUserProfileErrors);
                }

                console.warn("Updated User Profile (userIdentityId): ", updatedUserProfile);
            }
        }

    }

    /**
     * 
     * @param isAnyUserLoggedIn - serves for decision what type of auth is used to query data - guest/group
     * @param targetUserId 
     * @returns 
     */
    public static getPublicProfileByUserIdFromDb = async (isAnyUserLoggedIn: boolean, targetUserId: string) => {
        // TODO so many occurences from place creation - investigate
        // console.warn("Get public profile called for isUserLoggedin: ", isAnyUserLoggedIn, ", userId: ", targetUserId);

        const { data: foundUserProfile, errors: foundUserProfileErrors } = await dbClient.models.UserPublicProfile.get(
            {
                id: targetUserId
            },
            // Options
            {
                authMode: isAnyUserLoggedIn ? 'userPool' : undefined
            }
        )

        if (foundUserProfileErrors) {
            console.error("Get user profile errors: ", foundUserProfileErrors);
        }

        return foundUserProfile;
    }

}

